// Company-name casing + legal-suffix tidying for the company POA (owner, 2026-10-27).
// Based on CIPC-Registration/frontend/src/lib/companySuffix.js, extended because a POA can be
// for a CC or a public company too, not only a (Pty) Ltd:
//  - a suffix the client typed is tidied to its formal form: (Pty) Ltd, CC or Ltd;
//    NPC, Inc and SOC Ltd are recognised and kept; no suffix → "(Pty) Ltd" is added;
//  - exactly one suffix, never duplicated ("X (Pty) Ltd (Pty) Ltd", "X CC CC" → one);
//  - small joining words (and, of, the, ...) stay lower-case mid-name (owner);
//  - unlike CIPC, a name merely ENDING in "pty"/"prop" ("Empty", "Smart Prop") is never
//    mistaken for a half-typed "(Pty) Ltd" / "Proprietary Limited".

const JOINING_WORDS = new Set(['and', 'of', 'the', 'for', 'in', 'on', 'at', 'to', 'by', 'de', 'van', 'du', 'der', 'den']);

const capitalise = (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();

// Live-typing formatter (as CIPC): every word capitalised; words of 3 letters or fewer are
// treated as acronyms and fully upper-cased ("ict" -> "ICT"), except the legal suffix words
// (Pty, Ltd, Inc) and, mid-name, the joining words above.
export const formatCompanyNameCasing = (str) => {
  let wordIndex = 0;
  return str
    .replace(/^\s+/, '')
    .split(/(\s+)/)
    .map((token) => {
      if (token.trim() === '') return token;
      const first = wordIndex++ === 0;
      const suffixMatch = token.match(/^(\(?)(pty|ltd|inc)(\)?)(\.?)$/i);
      if (suffixMatch) {
        const [, open, word, close, dot] = suffixMatch;
        return open + capitalise(word) + close + dot;
      }
      // Joining words: lower-case mid-name, capitalised (not "THE"/"VAN") as the first word
      if (JOINING_WORDS.has(token.toLowerCase())) return first ? capitalise(token) : token.toLowerCase();
      if (token.replace(/[^A-Za-z]/g, '').length <= 3) return token.toUpperCase();
      // Each hyphenated part capitalised ("Smith-Jones"); "O'Brien" style kept
      return token
        .split('-')
        .map((part) => (/^[a-z]'[a-z]/i.test(part) ? part.charAt(0).toUpperCase() + "'" + capitalise(part.slice(2)) : capitalise(part)))
        .join('-');
    })
    .join('');
};

// Complete legal suffixes at the end of the name (case-insensitive) and their formal form.
const FULL_SUFFIXES = [
  { re: /\(?\s*\bpty\s*\)?\.?\s*(?:ltd|limited)\.?$/i, formal: '(Pty) Ltd' },
  { re: /\b(?:proprietary|prop\.?)\s+(?:limited|ltd\.?)$/i, formal: '(Pty) Ltd' },
  { re: /\bsoc\s+(?:ltd|limited)\.?$/i, formal: 'SOC Ltd' },
  { re: /\b(?:close\s+corporation|c\.?\s?c\.?)$/i, formal: 'CC' },
  { re: /\bnpc$/i, formal: 'NPC' },
  { re: /\b(?:incorporated|inc\.?)$/i, formal: 'Inc' },
  { re: /\b(?:limited|ltd\.?)$/i, formal: 'Ltd' },
];

// A half-typed or half-deleted "(Pty) Ltd" / "Proprietary Limited" at the end ("(Pty) Lt",
// "(Pty)", "(P", "(", "Pty", "Proprietary Limite", "Prop Lt"), cleaned up before "(Pty) Ltd"
// is added so it is never doubled. As on CIPC, "p"/"pt" only count inside an open bracket
// ("(P", "(Pt"); bare "pty"/"prop" must be whole words, and a bare "prop" only counts when
// followed by the start of Limited/Ltd.
const PARTIAL_PTY_RE = /\s*(?:\(\s*(?:pty|pt|p)?\)?\s*(?:ltd|lt|l)?|\bpty\s*(?:ltd|lt|l)?|\bproprietary\s*(?:limited|limite|limit|limi|lim|li|ltd|lt|l)?|\bprop\s+(?:limited|limite|limit|limi|lim|li|ltd|lt|l))\s*$/i;

// Called when the client leaves the field (and before sending): ensures the name ends with
// exactly one formal suffix.
export const normalizeCompanySuffix = (name) => {
  const trimmed = (name || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return trimmed;

  // Strip every complete suffix at the end (removes duplicates), remembering what was typed
  let rest = trimmed.replace(/[\s,]+$/, '');
  const found = [];
  for (let guard = 0; guard < 6; guard++) {
    const kind = FULL_SUFFIXES.find((k) => k.re.test(rest));
    if (!kind) break;
    found.push(kind.formal);
    rest = rest.replace(kind.re, '').replace(/[\s,]+$/, '');
  }

  let formal;
  if (found.length) {
    // "X Ltd (Pty) Ltd" and similar: the specific kind wins over a plain "Ltd"
    formal = found.find((f) => f !== 'Ltd') || 'Ltd';
  } else {
    rest = rest.replace(PARTIAL_PTY_RE, '').trim();
    formal = '(Pty) Ltd';
  }
  if (!rest) return formatCompanyNameCasing(trimmed); // the name was only a suffix: leave it
  return `${rest} ${formal}`;
};
